import { useState } from 'react'
import { Card, Button, Table, Badge, Form, Row, Col, Spinner, Alert } from 'react-bootstrap'
import { useQuery, useQueryClient } from 'react-query'
import { useAuth } from '../../hooks/useAuth'
import api from '../../services/api'
import { formatDateOnlyEsMX } from '../../utils/apiDateFormat'
import ReconciliationForm from './ReconciliationForm'
import ReconciliationDetail from './ReconciliationDetail'
import { formatCurrency } from '../../utils/formatters'

const BankReconciliation = () => {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [showDetail, setShowDetail] = useState(false)
  const [selectedReconciliation, setSelectedReconciliation] = useState(null)
  // null = el modal crea; con una conciliacion = el modal corrige esa. El formulario ya
  // sabia hacer PUT; lo que faltaba era abrirlo con algo distinto de null.
  const [editando, setEditando] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterEstado, setFilterEstado] = useState('all')
  const [filterBankAccount, setFilterBankAccount] = useState('')

  // Obtener cuentas bancarias para filtro
  const { data: bankAccounts } = useQuery(
    'bank-accounts-for-filter',
    async () => {
      const response = await api.get('/financial/bank-accounts/?is_active=true')
      return response.data.results || response.data || []
    }
  )

  // Obtener conciliaciones con filtros
  const { data: reconciliationsData, isLoading } = useQuery(
    ['bank-reconciliations', filterEstado, filterBankAccount],
    async () => {
      const params = new URLSearchParams()
      if (filterEstado !== 'all') params.append('estado', filterEstado)
      if (filterBankAccount) params.append('bank_account_id', filterBankAccount)
      params.append('ordering', '-reconciliation_date')
      
      const response = await api.get(`/financial/bank-reconciliations/?${params.toString()}`)
      return response.data.results || response.data || []
    },
    { enabled: !!user }
  )

  const reconciliations = reconciliationsData || []

  // Filtrar conciliaciones por búsqueda
  const filteredReconciliations = reconciliations.filter(reconciliation => {
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase()
      return (
        reconciliation.bank_account_name?.toLowerCase().includes(searchLower) ||
        reconciliation.id?.toString().includes(searchLower)
      )
    }
    return true
  })

  // Calcular estadísticas
  const totalMovimientos = filteredReconciliations.reduce((sum, r) => sum + (r.movimientos?.length || 0), 0)
  const movimientosPendientes = filteredReconciliations.reduce((sum, r) => {
    return sum + (r.movimientos?.filter(m => m.estado === 'pendiente').length || 0)
  }, 0)

  const handleViewDetail = (reconciliation) => {
    setSelectedReconciliation(reconciliation)
    setShowDetail(true)
  }

  const handleEdit = (reconciliation) => {
    setEditando(reconciliation)
    setShowForm(true)
  }

  const handleCreateSuccess = (data) => {
    queryClient.invalidateQueries('bank-reconciliations')
    // Abrir detalle de la nueva conciliación
    if (data?.id) {
      setSelectedReconciliation(data)
      setShowDetail(true)
    }
  }

  // Al corregir una captura NO se abre el detalle: quien edita quiere ver la lista con el
  // dato ya bien, no entrar al detalle como si acabara de crear la conciliacion.
  const handleEditSuccess = () => {
    queryClient.invalidateQueries('bank-reconciliations')
  }

  const getEstadoBadge = (estado) => {
    const badges = {
      'abierta': { bg: 'info', text: 'Abierta' },
      'en_proceso': { bg: 'warning', text: 'En Proceso' },
      'cerrada': { bg: 'success', text: 'Cerrada' }
    }
    const badge = badges[estado] || { bg: 'secondary', text: estado }
    return <Badge bg={badge.bg}>{badge.text}</Badge>
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3>Conciliación Bancaria</h3>
        <Button variant="primary" onClick={() => setShowForm(true)}>
          <i className="bi bi-plus-circle me-1"></i>
          Nueva Conciliación
        </Button>
      </div>

      {/* Filtros y búsqueda */}
      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Buscar</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Buscar por cuenta bancaria o ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Estado</Form.Label>
                <Form.Select
                  value={filterEstado}
                  onChange={(e) => setFilterEstado(e.target.value)}
                >
                  <option value="all">Todos</option>
                  <option value="abierta">Abierta</option>
                  <option value="en_proceso">En Proceso</option>
                  <option value="cerrada">Cerrada</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Cuenta Bancaria</Form.Label>
                <Form.Select
                  value={filterBankAccount}
                  onChange={(e) => setFilterBankAccount(e.target.value)}
                >
                  <option value="">Todas</option>
                  {bankAccounts?.map(account => (
                    <option key={account.id} value={account.id}>
                      {account.name} - {account.account_number}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Estadísticas */}
      <Row className="mb-4">
        <Col md={3}>
          <Card>
            <Card.Body>
              <Card.Title className="text-muted small">Conciliaciones</Card.Title>
              <h3>{filteredReconciliations.length}</h3>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card>
            <Card.Body>
              <Card.Title className="text-muted small">Total Movimientos</Card.Title>
              <h3>{totalMovimientos}</h3>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card>
            <Card.Body>
              <Card.Title className="text-muted small">Pendientes</Card.Title>
              <h3 className="text-warning">{movimientosPendientes}</h3>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card>
            <Card.Body>
              <Card.Title className="text-muted small">Abiertas</Card.Title>
              <h3 className="text-info">
                {filteredReconciliations.filter(r => r.estado === 'abierta' || r.estado === 'en_proceso').length}
              </h3>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Lista de conciliaciones */}
      {isLoading ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Cargando...</span>
          </Spinner>
        </div>
      ) : (
        <Card>
          <Card.Header>
            <h5 className="mb-0">Conciliaciones ({filteredReconciliations.length})</h5>
          </Card.Header>
          <Card.Body>
            {filteredReconciliations.length > 0 ? (
              <Table responsive striped hover>
                <thead>
                  <tr>
                    <th>Cuenta Bancaria</th>
                    <th>Fecha Conciliación</th>
                    <th>Saldo Estado Cuenta</th>
                    <th>Saldo en Libros</th>
                    <th>Diferencia</th>
                    <th>Estado</th>
                    <th>Movimientos</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredReconciliations.map((reconciliation) => {
                    const movimientosCount = reconciliation.movimientos?.length || 0
                    const diferencia = parseFloat(reconciliation.difference || 0)
                    return (
                      <tr key={reconciliation.id}>
                        <td>{reconciliation.bank_account_name || 'N/A'}</td>
                        <td>{formatDateOnlyEsMX(reconciliation.reconciliation_date, {}, '—')}</td>
                        <td>
                          {formatCurrency(reconciliation.statement_balance || 0)}
                        </td>
                        <td>
                          {formatCurrency(reconciliation.book_balance || 0)}
                        </td>
                        <td className={Math.abs(diferencia) < 0.01 ? 'text-success' : 'text-warning'}>
                          {formatCurrency(Math.abs(diferencia))}
                        </td>
                        <td>{getEstadoBadge(reconciliation.estado)}</td>
                        <td>{movimientosCount}</td>
                        <td>
                          <div className="d-flex gap-2">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleViewDetail(reconciliation)}
                              style={{ minWidth: '100px' }}
                            >
                              <i className="bi bi-eye me-1"></i>
                              <span>Ver Detalle</span>
                            </Button>
                            {/* Una cerrada no se edita: su diferencia ya se explico y se dio
                                por buena. El backend lo valida igual. */}
                            {reconciliation.estado !== 'cerrada' && (
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => handleEdit(reconciliation)}
                                title="Corregir fecha o saldo del estado de cuenta"
                              >
                                <i className="bi bi-pencil me-1"></i>
                                <span>Editar</span>
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </Table>
            ) : (
              <Alert variant="info">
                {searchTerm || filterEstado !== 'all' || filterBankAccount
                  ? 'No se encontraron conciliaciones con los filtros aplicados'
                  : 'No hay conciliaciones registradas. Crea una nueva conciliación para comenzar.'}
              </Alert>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Modal de formulario de conciliación */}
      <ReconciliationForm
        show={showForm}
        onHide={() => {
          setShowForm(false)
          setEditando(null)   // unico lugar que limpia: el modal solo se cierra por aqui
        }}
        reconciliation={editando}
        onSuccess={editando ? handleEditSuccess : handleCreateSuccess}
      />

      {/* Vista detallada de conciliación */}
      {showDetail && selectedReconciliation && (
        <ReconciliationDetail
          reconciliation={selectedReconciliation}
          onClose={() => {
            setShowDetail(false)
            setSelectedReconciliation(null)
          }}
          onRefresh={() => {
            queryClient.invalidateQueries('bank-reconciliations')
            // Recargar la conciliación actualizada
            api.get(`/financial/bank-reconciliations/${selectedReconciliation.id}/`).then(res => {
              setSelectedReconciliation(res.data)
            })
          }}
        />
      )}
    </div>
  )
}

export default BankReconciliation

